import type { AgentInputItem } from '../types';

/**
 * 一个函数，用于在模型调用前将会话历史与新输入条目进行合并。
 */
export type SessionInputCallback = (
  historyItems: AgentInputItem[],
  newItems: AgentInputItem[],
) => AgentInputItem[] | Promise<AgentInputItem[]>;

/**
 * 表示用于存储对话历史的持久化会话存储的接口。
 */
export interface Session {
  /**
   * 确保并返回此会话的标识符。
   */
  getSessionId(): Promise<string>;

  /**
   * 从对话历史中检索条目。
   *
   * @param limit - 要返回的最大条目数。提供此参数后，应按时间顺序返回最近的 {@link limit} 条记录。
   */
  getItems(limit?: number): Promise<AgentInputItem[]>;

  /**
   * 可选择在将存储的历史条目发回模型之前对其进行改写。
   *
   * 会话实现可以利用此方法来剥离由服务商管理的重放元数据，同时保留其公开的 `getItems()` 结构用于界面展示和删除工作流。
   */
  prepareHistoryItemForModelInput?(item: AgentInputItem): AgentInputItem;

  /**
   * 向对话历史追加新条目。
   *
   * @param items - 要添加到会话历史的条目。
   */
  addItems(items: AgentInputItem[]): Promise<void>;

  /**
   * 从对话历史中移除并返回最近的一条记录（如果存在）。
   */
  popItem(): Promise<AgentInputItem | undefined>;

  /**
   * 清除属于该会话的所有条目并重置其状态。
   */
  clearSession(): Promise<void>;
}

export type SessionTurnRole = 'user' | 'assistant';
export type SessionTurnStatus = 'in_progress' | 'completed' | 'interrupted';
export type SessionItemOrigin =
  | 'user'
  | 'assistant'
  | 'tool'
  | 'system'
  | 'synthetic';

export type SessionConversationTurn = {
  id: string;
  role: SessionTurnRole;
  branchId: string;
  itemIds: string[];
  status: SessionTurnStatus;
  createdAt: string;
  updatedAt: string;
  parentTurnId?: string;
};

export type SessionConversationItemMeta = {
  itemId: string;
  turnId: string;
  branchId: string;
  origin: SessionItemOrigin;
};

export type SessionConversationGraphSnapshot = {
  activeBranchId: string;
  turns: Record<string, SessionConversationTurn>;
  itemMeta: Record<string, SessionConversationItemMeta>;
};

export type SessionConversationGraphUpdate = {
  activeBranchId?: string;
  turns?: SessionConversationTurn[];
  itemMeta?: SessionConversationItemMeta[];
  removeItemIds?: string[];
};

export interface SessionConversationGraphAwareSession extends Session {
  getConversationGraph():
    | Promise<SessionConversationGraphSnapshot | undefined>
    | SessionConversationGraphSnapshot
    | undefined;
  updateConversationGraph(
    update: SessionConversationGraphUpdate,
  ): Promise<void> | void;
}

export function isSessionConversationGraphAwareSession(
  session: Session | undefined,
): session is SessionConversationGraphAwareSession {
  return (
    !!session &&
    typeof (session as SessionConversationGraphAwareSession)
      .getConversationGraph === 'function' &&
    typeof (session as SessionConversationGraphAwareSession)
      .updateConversationGraph === 'function'
  );
}

export function createEmptyConversationGraph(
  activeBranchId = 'main',
): SessionConversationGraphSnapshot {
  return {
    activeBranchId,
    turns: {},
    itemMeta: {},
  };
}

export function applyConversationGraphUpdate(
  graph: SessionConversationGraphSnapshot | undefined,
  update: SessionConversationGraphUpdate,
): SessionConversationGraphSnapshot {
  const next: SessionConversationGraphSnapshot = structuredClone(
    graph ?? createEmptyConversationGraph(update.activeBranchId),
  );
  if (update.activeBranchId) {
    next.activeBranchId = update.activeBranchId;
  }
  for (const turn of update.turns ?? []) {
    next.turns[turn.id] = structuredClone(turn);
  }
  for (const meta of update.itemMeta ?? []) {
    next.itemMeta[meta.itemId] = structuredClone(meta);
  }
  for (const itemId of update.removeItemIds ?? []) {
    const meta = next.itemMeta[itemId];
    delete next.itemMeta[itemId];
    if (meta) {
      const turn = next.turns[meta.turnId];
      if (turn) {
        turn.itemIds = turn.itemIds.filter((id) => id !== itemId);
        turn.updatedAt = new Date().toISOString();
      }
    }
  }
  return next;
}

export type SessionHistoryReplaceFunctionCallMutation = {
  type: 'replace_function_call';
  callId: string;
  replacement: Extract<AgentInputItem, { type: 'function_call' }>;
};

export type SessionHistoryMutation = SessionHistoryReplaceFunctionCallMutation;

export type SessionHistoryRewriteArgs = {
  mutations: SessionHistoryMutation[];
};

export interface SessionHistoryRewriteAwareSession extends Session {
  applyHistoryMutations(args: SessionHistoryRewriteArgs): Promise<void> | void;
}

export function isSessionHistoryRewriteAwareSession(
  session: Session | undefined,
): session is SessionHistoryRewriteAwareSession {
  return (
    !!session &&
    typeof (session as SessionHistoryRewriteAwareSession)
      .applyHistoryMutations === 'function'
  );
}
